import { useEffect, useState } from "react";

import apiClient from "../../../services/apiClient";
import "./PromotionPage.css";

// 학원 관리 > 프로모션(2026-09-30). '담당 학교와 교과서' 한 줄이 무료 자료 링크 하나다.
// 줄마다 블로그·카페·인스타·쓰레드·당근 글(10가지 중 하나)과 세로·정사각 이미지를 만들어 둔다.
// 글마다 주소 끝(?c=blog 등)이 달라 신청이 어디서 왔는지 승인 목록과 텔레그램 알림에 뜬다.
// 링크로 들어온 분이 닉네임을 적어 신청하면 아래 승인 목록에서 승인한다(학교·교과서가 함께 보인다).
// 자료가 아직 없는 교과서·부교재도 링크는 미리 만든다. 받기는 자료가 올라간 뒤 열린다.
// 서버: api/echo/academy_promo_views.py, 글: api/echo/academy_promo_texts.py

const STATUS_LABEL = { pending: "승인 대기", approved: "승인", rejected: "거절" };
const CUSTOM_TEXTBOOK = "__custom__";
const PLATFORM_LIST = [
  { key: "blog_list", label: "블로그" },
  { key: "cafe_list", label: "카페" },
  { key: "insta_list", label: "인스타" },
  { key: "threads_list", label: "쓰레드" },
  { key: "daangn_list", label: "당근" },
];

const formatTime = (value) =>
  new Date(value).toLocaleString("ko-KR", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" });

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    window.prompt("아래 글을 복사해 주세요", text);
  }
};

const toEditRow = (row) => ({
  key: row.key,
  school_name: row.school_name,
  grade: row.grade,
  textbook_code: row.textbook_code || (row.custom_textbook ? CUSTOM_TEXTBOOK : ""),
  custom_textbook: row.custom_textbook || "",
});

const PromotionPage = () => {
  const [data, setData] = useState(null);
  const [region, setRegion] = useState("");
  const [editRowList, setEditRowList] = useState([]);
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState("");
  const [openKey, setOpenKey] = useState("");
  const [variantOffsetMap, setVariantOffsetMap] = useState({});
  const [missingOpen, setMissingOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const apply = (next) => {
    setData(next);
    setRegion(next.region || "");
    setEditRowList((next.row_list || []).map(toEditRow));
  };

  const load = () => apiClient.get("/api/v1/echo/teacher/promo", { withAuth: true }).then((response) => apply(response.data));

  useEffect(() => {
    load();
  }, []);

  if (!data) return null;

  const savedRowMap = Object.fromEntries((data.row_list || []).map((row) => [row.key, row]));
  const textbookList = data.textbook_list || [];

  const updateRow = (index, patch) =>
    setEditRowList((prev) => prev.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  const addRow = () => setEditRowList((prev) => [...prev, { school_name: "", grade: "", textbook_code: "", custom_textbook: "" }]);
  const removeRow = (index) => setEditRowList((prev) => prev.filter((_, rowIndex) => rowIndex !== index));

  const save = async () => {
    setMessage("");
    const incomplete = editRowList.some(
      (row) =>
        !row.school_name.trim() ||
        !row.grade ||
        !row.textbook_code ||
        (row.textbook_code === CUSTOM_TEXTBOOK && !row.custom_textbook.trim())
    );
    if (incomplete) {
      setMessage("학교 이름, 학년, 교과서(목록에 없으면 직접 적기)를 모두 채워 주세요. 빈 줄은 삭제해 주세요.");
      return;
    }
    setIsSaving(true);
    try {
      const response = await apiClient.put(
        "/api/v1/echo/teacher/promo",
        {
          region,
          row_list: editRowList.map((row) => ({
            key: row.key,
            school_name: row.school_name,
            grade: row.grade,
            textbook_code: row.textbook_code === CUSTOM_TEXTBOOK ? "" : row.textbook_code,
            custom_textbook: row.textbook_code === CUSTOM_TEXTBOOK ? row.custom_textbook : "",
          })),
        },
        { withAuth: true }
      );
      apply(response.data);
      setMessage("저장했어요. 줄마다 링크와 이미지 · 글이 만들어졌어요.");
      if ((response.data.row_list || []).some((row) => !row.file_ready)) setMissingOpen(true);
    } catch (caught) {
      setMessage(caught.response?.data?.error || "저장하지 못했어요.");
    } finally {
      setIsSaving(false);
    }
  };

  const copy = async (id, text) => {
    await copyText(text);
    setCopied(id);
    setTimeout(() => setCopied(""), 1500);
  };

  const variantOf = (row) => ((row.first_index || 0) + (variantOffsetMap[row.key] || 0)) % ((row.blog_list || []).length || 1);
  const nextVariant = (key) => setVariantOffsetMap((prev) => ({ ...prev, [key]: (prev[key] || 0) + 1 }));

  const decide = async (requestId, action) => {
    await apiClient.post("/api/v1/echo/teacher/promo/decide", { request_id: requestId, action }, { withAuth: true });
    load();
  };

  const requestList = data.request_list || [];
  const pendingList = requestList.filter((one) => one.status === "pending");
  const shownList = showAll ? requestList : pendingList;

  return (
    <div className="promo-page">
      <div className="section-title">프로모션</div>
      <p className="promo-page__lead">
        담당 학교와 교과서를 적으면 줄마다 <b>무료 자료 링크</b>와 <b>블로그 · 인스타 · 쓰레드 · 당근 글</b>, <b>이미지</b>가 만들어져요.
        글에는 지역 · 학교 · 학원 · 출판사가 들어가 검색에 잘 걸려요. 올린 뒤 들어온 신청은 아래에서 <b>승인</b>만 누르면 돼요.
      </p>

      <section className="promo-page__card">
        <div className="promo-page__card-title">담당 학교와 교과서</div>
        <label className="promo-page__region">
          <span>지역</span>
          <input value={region} placeholder="예: 대전 유성구" onChange={(event) => setRegion(event.target.value)} />
        </label>

        {editRowList.map((row, index) => {
          const saved = row.key ? savedRowMap[row.key] : null;
          const variant = saved ? variantOf(saved) : 0;
          return (
            <div key={row.key || `new-${index}`} className="promo-page__row-block">
              <div className="promo-page__school-row">
                <input
                  value={row.school_name}
                  placeholder="학교 이름 (예: 어은중)"
                  onChange={(event) => updateRow(index, { school_name: event.target.value })}
                />
                <select
                  value={row.grade}
                  onChange={(event) =>
                    updateRow(index, {
                      grade: event.target.value,
                      textbook_code: row.textbook_code === CUSTOM_TEXTBOOK ? CUSTOM_TEXTBOOK : "",
                    })
                  }
                >
                  <option value="">학년</option>
                  {(data.grade_list || []).map((grade) => (
                    <option key={grade} value={grade}>{grade}</option>
                  ))}
                </select>
                <select
                  value={row.textbook_code}
                  disabled={!row.grade}
                  onChange={(event) => updateRow(index, { textbook_code: event.target.value })}
                >
                  <option value="">{row.grade ? "교과서 선택" : "학년을 먼저 고르세요"}</option>
                  {textbookList
                    .filter((book) => book.grade === row.grade)
                    .map((book) => (
                      <option key={book.code} value={book.code}>
                        {book.label} ({book.subject})
                      </option>
                    ))}
                  <option value={CUSTOM_TEXTBOOK}>목록에 없어요 (직접 적기 · 부교재)</option>
                </select>
                <button type="button" className="promo-page__row-remove" onClick={() => removeRow(index)}>
                  삭제
                </button>
              </div>
              {row.textbook_code === CUSTOM_TEXTBOOK && (
                <input
                  className="promo-page__custom"
                  value={row.custom_textbook}
                  placeholder="교과서나 부교재 이름 (예: 능률 양현권, 리딩튜터 주니어 2)"
                  onChange={(event) => updateRow(index, { custom_textbook: event.target.value })}
                />
              )}

              {!saved && <p className="promo-page__help">저장하면 이 줄의 링크와 이미지 · 글이 만들어져요.</p>}
              {saved && (
                <div className="promo-page__share">
                  <div className="promo-page__share-head">
                    <b>{saved.title}</b>
                    {!saved.file_ready && (
                      <button type="button" className="promo-page__badge promo-page__badge--wait" onClick={() => setMissingOpen(true)}>
                        자료 준비 중
                      </button>
                    )}
                    {saved.pending_count > 0 && <em className="promo-page__badge promo-page__badge--wait">대기 {saved.pending_count}</em>}
                    {saved.approved_count > 0 && <em className="promo-page__badge promo-page__badge--ok">승인 {saved.approved_count}</em>}
                  </div>
                  {!saved.file_ready && <p className="promo-page__missing">{data.material_missing_message}</p>}
                  <div className="promo-page__actions">
                    {PLATFORM_LIST.map((platform) => (
                      <button
                        key={platform.key}
                        type="button"
                        onClick={() => copy(`${saved.key}-${platform.key}`, (saved[platform.key] || [])[variant] || "")}
                      >
                        {copied === `${saved.key}-${platform.key}` ? "복사했어요" : `${platform.label} 글 복사`}
                      </button>
                    ))}
                    <a className="promo-page__image-link" href={saved.card_portrait_url} download target="_blank" rel="noreferrer">
                      세로 이미지
                    </a>
                    <a className="promo-page__image-link" href={saved.card_square_url} download target="_blank" rel="noreferrer">
                      정사각 이미지
                    </a>
                    <button type="button" onClick={() => copy(`${saved.key}-url`, saved.url)}>
                      {copied === `${saved.key}-url` ? "복사했어요" : "링크 복사"}
                    </button>
                    <button type="button" className="promo-page__ghost" onClick={() => nextVariant(saved.key)}>
                      다른 글로 ({variant + 1}/{(saved.blog_list || []).length})
                    </button>
                    <button type="button" className="promo-page__ghost" onClick={() => setOpenKey(openKey === saved.key ? "" : saved.key)}>
                      {openKey === saved.key ? "미리보기 접기" : "미리보기"}
                    </button>
                  </div>
                  {openKey === saved.key && (
                    <div className="promo-page__preview">
                      {PLATFORM_LIST.map((platform) => (
                        <div key={platform.key}>
                          <b>{platform.label}</b>
                          <pre>{(saved[platform.key] || [])[variant]}</pre>
                        </div>
                      ))}
                      <div className="promo-page__cards">
                        <b>함께 올릴 이미지</b>
                        <div>
                          <img src={saved.card_portrait_url} alt="세로 이미지" loading="lazy" />
                          <img src={saved.card_square_url} alt="정사각 이미지" loading="lazy" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        <div className="promo-page__row-foot">
          <button type="button" className="promo-page__row-add" onClick={addRow}>+ 학교 추가</button>
          <button type="button" className="promo-page__primary" disabled={isSaving} onClick={save}>
            {isSaving ? "저장 중..." : "저장하기"}
          </button>
        </div>
        {message && <p className="promo-page__message">{message}</p>}
      </section>

      {missingOpen && (
        <div className="promo-page__popup" role="dialog" aria-modal="true" onClick={() => setMissingOpen(false)}>
          <div className="promo-page__popup-card" onClick={(event) => event.stopPropagation()}>
            <b>아직 지문 자료가 없어요</b>
            <p>
              링크와 이미지 · 글은 미리 만들어 두었어요.
              <br />
              다운로드는 자료가 준비된 뒤에 열려요.
              <br />
              <strong>지문을 유현쌤께 꼭 전달해 주세요!</strong>
            </p>
            <button type="button" className="promo-page__primary" onClick={() => setMissingOpen(false)}>확인</button>
          </div>
        </div>
      )}

      <section className="promo-page__card">
        <div className="promo-page__card-head">
          <div className="promo-page__card-title">승인 목록 {pendingList.length > 0 ? `· 승인 대기 ${pendingList.length}명` : ""}</div>
          <button type="button" className="promo-page__toggle" onClick={() => setShowAll((prev) => !prev)}>
            {showAll ? "승인 대기만 보기" : `전체 보기 (${requestList.length})`}
          </button>
        </div>
        <p className="promo-page__help">
          승인하면 그분이 신청한 기기(같은 브라우저나 같은 인터넷)에서 바로 받을 수 있어요. 승인한 뒤 <b>답글 복사</b>로 댓글에 답을 달아 주세요.
        </p>
        {shownList.length === 0 ? (
          <p className="promo-page__help">{showAll ? "아직 신청이 없어요." : "승인을 기다리는 신청이 없어요."}</p>
        ) : (
          shownList.map((one) => (
            <div key={one.id} className="promo-page__row">
              <div className="promo-page__who">
                <b>{one.title}</b>
                <span>
                  {one.nickname}
                  {one.source_label ? ` · ${one.source_label}에서` : ""} · {formatTime(one.created_at)}
                  {one.download_count > 0 ? ` · ${one.download_count}번 받음` : ""}
                </span>
              </div>
              <span className={`promo-page__status promo-page__status--${one.status}`}>{STATUS_LABEL[one.status]}</span>
              <div className="promo-page__row-actions">
                {one.status !== "approved" && (
                  <button type="button" className="promo-page__approve" onClick={() => decide(one.id, "approve")}>승인</button>
                )}
                {one.status === "approved" && (
                  <button type="button" className="promo-page__reject" onClick={() => copy(`reply-${one.id}`, one.reply_text)}>
                    {copied === `reply-${one.id}` ? "복사했어요" : "답글 복사"}
                  </button>
                )}
                {one.status !== "rejected" && (
                  <button type="button" className="promo-page__reject" onClick={() => decide(one.id, "reject")}>거절</button>
                )}
              </div>
            </div>
          ))
        )}
      </section>
    </div>
  );
};

export default PromotionPage;
